import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Subject, takeUntil, forkJoin, Observable, of } from 'rxjs';
import { DOC_STATE, DOC_UPLOAD } from 'src/app/constants/endpoint.const';
import { DocumentRow, SlaFileArray, SlaFileDataResponse } from 'src/app/models/integration.model';
import { BaseService } from 'src/app/services/base.service';
import { documents } from 'src/app/constants/integrations.const';

@Component({
  selector: 'app-sla-submission',
  standalone: false,
  templateUrl: './sla-submission.component.html',
  styleUrl: './sla-submission.component.scss',
})
export class SlaSubmissionComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  loading: boolean = true;
  SlaFiles!: FormGroup;

  fileArrays: Record<string, SlaFileArray> = {};
  previews: Record<string, { type: 'pdf' | 'link'; url: string; name: string } | null> = {};

  isConfirmBack: boolean = false;
  @Output() openModalConfirmBack = new EventEmitter<boolean>();

  documents: DocumentRow[] = documents;
  serviceType: string = '';

  private get serviceId(): number {
    return this.serviceType === 'shipping' ? 1 : 2;
  }

  constructor(
    private router: Router,
    private baseService: BaseService,
    private fb: FormBuilder,
    private message: NzMessageService,
    private route: ActivatedRoute,
  ) {
    this.documents.forEach(doc => this.fileArrays[doc.key] = []);
  }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.route.queryParams.subscribe(params => {
      this.serviceType = params['service_type'];
    });
    this.initForm();
    this.getFiles();
    setTimeout(() => { this.loading = false; }, 1000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    Object.values(this.previews).forEach(preview => {
      if (preview?.type === 'pdf' && preview.url.startsWith('blob:')) {
        URL.revokeObjectURL(preview.url);
      }
    });
  }

  initForm(): void {
    const groupConfig = this.documents.reduce(
      (controls, doc) => ({
        ...controls,
        [doc.key]: [null],
        [doc.key + '_note']: [null, [Validators.minLength(5), Validators.maxLength(250)]],
      }),
      {}
    );

    this.SlaFiles = this.fb.group(groupConfig, {
      validators: [this.requiredDocumentValidator.bind(this)]
    });
  }

  requiredDocumentValidator(formGroup: FormGroup) {
    this.documents.forEach(doc => {
      if (!doc.isRequired) return;

      const fileControl = formGroup.get(doc.key);
      const noteControl = formGroup.get(doc.key + '_note');
      const hasFile = !!fileControl?.value;
      const hasValidNote = !!noteControl?.value && noteControl.value.trim().length >= 5;

      if (!hasFile && !hasValidNote) {
        fileControl?.setErrors({ required: true });
      } else {
        const errors = fileControl?.errors;
        if (errors) {
          delete errors['required'];
          fileControl?.setErrors(Object.keys(errors).length > 0 ? errors : null);
        }
      }
    });
    return null;
  }

  handleBack(): void {
    this.isConfirmBack = true;
  }

  closeConfirmBack(): void {
    this.isConfirmBack = false;
  }

  handleNotes(event: Event, key: string): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.SlaFiles.controls[key + '_note'].setValue(value.trim() === '' ? '' : value);
  }

  focusNext(currentIndex: number): void {
    const nextInput = document.getElementById('note_' + (currentIndex + 1));
    if (nextInput) {
      nextInput.focus();
    }
  }

  triggerFileInput(key: string): void {
    const fileInput = document.getElementById('fileupload_' + key) as HTMLInputElement;
    fileInput?.click();
  }

  handleFileUpload(event: Event, key: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (file.size > 5000 * 1024) {
      this.message.error('File size exceeds limit');
      return;
    }
    if (fileExtension !== 'pdf') {
      this.message.error('Invalid file format');
      return;
    }
    if (!this.fileArrays[key]) {
      console.error('Unknown key:', key);
      return;
    }

    this.fileArrays[key] = [file];
    this.SlaFiles.patchValue({ [key]: file });
    this.updatePreview(key);
  }

  getFiles(): void {
    this.baseService.getData(`${DOC_UPLOAD}/service-level-agreement?service_types_id=${this.serviceId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { data: SlaFileDataResponse[] }) => {
          res.data.forEach((file) => {
            if (!this.fileArrays[file.document_type]) return;

            this.fileArrays[file.document_type] = [{
              url: file.uploaded_file_path,
              name: file.file_name,
              notes: file.notes
            }];

            this.SlaFiles.patchValue({
              [file.document_type]: file.uploaded_file_path,
              [file.document_type + '_note']: file.notes,
            });
            this.updatePreview(file.document_type);
          });
        },
        error: (err) => {
          this.message.error(err.error.meta.errors || 'Failed to get files');
        }
      });
  }

  resetFileInput(docKey: string): void {
    const inputElement = document.getElementById('fileupload_' + docKey) as HTMLInputElement;
    if (inputElement) inputElement.value = '';
  }

  deleteFile(key: string): void {
    const targetArray = this.fileArrays[key];
    if (!targetArray?.length) return;

    const file = targetArray[0];

    if (file instanceof File) {
      this.fileArrays[key] = [];
      this.SlaFiles.patchValue({ [key]: null });
      this.updatePreview(key);
      return;
    }

    if (file.url) {
      this.baseService.deleteData(DOC_UPLOAD + '/' + key + '?service_types_id=' + this.serviceId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.fileArrays[key] = [];
            this.SlaFiles.patchValue({ [key]: null });
            this.updatePreview(key);
            this.message.success('Document deleted successfully');
          },
          error: (err) => {
            this.message.error(err.error.meta.errors || 'Failed to delete document');
          }
        });
    }
  }

  updatePreview(key: string): void {
    const oldPreview = this.previews[key];
    if (oldPreview?.type === 'pdf' && oldPreview.url.startsWith('blob:')) {
      URL.revokeObjectURL(oldPreview.url);
    }

    const targetArray = this.fileArrays[key];
    if (!targetArray?.length) {
      this.previews[key] = null;
      return;
    }

    const file = targetArray[0];
    if (file instanceof File) {
      this.previews[key] = file.type === 'application/pdf'
        ? { type: 'pdf', url: URL.createObjectURL(file), name: file.name }
        : { type: 'link', url: '', name: file.name };
    } else if (file?.url) {
      this.previews[key] = { type: 'pdf', url: file.url, name: file.name || 'Download File' };
    } else {
      this.previews[key] = null;
    }
  }

  getUploadedFileName(key: string): string {
    return this.previews[key]?.name || '';
  }

  getUploadedFilePath(key: string): string {
    return this.previews[key]?.url || '';
  }

  handleFilePreview(key: string): { type: 'pdf' | 'link'; url: string; name: string } | null {
    return this.previews[key] || null;
  }

  uploadDocument(documentType: string, fileArray: SlaFileArray): Observable<any> {
    const noteValue = this.SlaFiles.get(documentType + '_note')?.value || '';
    if (fileArray.length === 0 && !noteValue) return of(null);

    const formdata = new FormData();
    formdata.append('document_type', documentType);
    formdata.append('notes', noteValue);
    formdata.append('service_types_id', this.serviceId.toString());

    if (fileArray.length > 0 && fileArray[0] instanceof File) {
      formdata.append('file', fileArray[0]);
    }

    return this.baseService.postData(DOC_UPLOAD, formdata);
  }

  onSubmit(): void {
    if (this.SlaFiles.invalid) {
      this.message.error('Please upload all required documents.');
      return;
    }

    const uploadObservables = this.documents.map(doc =>
      this.uploadDocument(doc.key, this.fileArrays[doc.key] || [])
    );

    forkJoin(uploadObservables)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.message.success('Documents uploaded successfully');
          this.updateState();
        },
        error: (err) => {
          this.message.error(err.error?.meta?.errors || 'Failed to upload documents');
        }
      });
  }

  updateState(): void {
    const params = { document_type: 'SLA', state: 4, service_types_id: this.serviceId };
    this.baseService.putData(`${DOC_STATE}`, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.router.navigate(['/document']),
        error: (err) => this.message.error(err.error.meta.errors || 'Failed to update document state'),
      });
  }
}

